import React, { FC, useState } from "react";
import { useIntl } from "react-intl";
import { format, parseISO } from "date-fns";
import { CustomerMergeSearchResult } from "@Shape-Digital/kudzu-data/lib/types/actions";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Autocomplete from "../../Unknown/Autocomplete";
import TextField from "../../Unknown/TextField";
import Typography from "../../Unknown/Typography";
import Box from "../../Unknown/Box";
import { formatInternational } from "../../../common/phoneNumberHelpers";
import InputAdornment from "../../Unknown/InputAdornment";
import CircularProgress from "../../Unknown/CircularProgress";
import useSearchCustomersForMerge from "./useSearchCustomersForMerge";
import messages from "./messages";

// TODO: drop once the published @Shape-Digital/kudzu-data has `dateBirth` on
// CustomerMergeSearchResult (backend already returns it).
type CustomerSearchOption = CustomerMergeSearchResult & {
  dateBirth?: string | null;
};

type AddCustomerToMergeSearchProps = {
  alreadySelectedIds: string[];
  onSelect: (customer: CustomerMergeSearchResult) => void;
};

const AddCustomerToMergeSearch: FC<AddCustomerToMergeSearchProps> = ({
  alreadySelectedIds,
  onSelect,
}) => {
  const { formatMessage } = useIntl();
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { results, isLoading } = useSearchCustomersForMerge(inputValue);

  return (
    <Autocomplete
      options={results as CustomerSearchOption[]}
      loading={isLoading}
      open={isOpen && !isLoading}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      // Uncontrolled selection value — this widget is a search-and-add field,
      // not a persisted single-select, so we never want it to "keep" a picked
      // option (see onChange, which clears inputValue and forwards the pick).
      value={null}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        // MUI fires onInputChange with reason "reset" right after onChange
        // sets inputValue to "" (to resync the field with the picked option's
        // label) — without this guard, that resync always wins and the field
        // gets stuck showing the selected option, blocking further searches.
        if (reason === "input") {
          setInputValue(value);
        }
      }}
      getOptionLabel={(option) =>
        `${option.lastName}, ${option.firstName} (${option.email})`
      }
      getOptionDisabled={(option) => alreadySelectedIds.includes(option.id)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, value) => {
        if (value) {
          onSelect(value);
          setInputValue("");
        }
      }}
      noOptionsText={
        inputValue.length < 3
          ? formatMessage(messages.searchMinChars)
          : formatMessage(messages.searchNoResults)
      }
      forcePopupIcon={false}
      // Global theme sets MuiPaper elevation to 0 by default; MUI's Select/Menu
      // dropdowns pass their own elevation explicitly so they're unaffected,
      // but Autocomplete's popup Paper isn't — without this it renders flat,
      // with no shadow/border, and blends into the page.
      componentsProps={{ paper: { elevation: 8 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={formatMessage(messages.searchLabel)}
          InputProps={{
            ...params.InputProps,
            // Design: while loading, a primary-colour spinner sits to the LEFT
            // of the search icon (the icon stays). Also visible once the popup
            // closes, so a slow search never looks unresponsive.
            endAdornment: (
              <InputAdornment position="end">
                <Box display="flex" alignItems="center" gap="8px">
                  {isLoading && <CircularProgress size={24} />}
                  <SearchOutlinedIcon color="action" />
                </Box>
              </InputAdornment>
            ),
          }}
        />
      )}
      // Figma "Customer Search" component (node 2374:33435, "Search results"
      // variant): each result stacks name + email + phone on separate lines
      // (not combined with a separator), DOB under the name — padding/hover come from MUI's
      // Autocomplete option defaults, which already match the spec.
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box display="flex" flexDirection="column" gap="2px">
            <Typography variant="body1">
              {option.lastName}, {option.firstName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.dateBirth
                ? format(parseISO(option.dateBirth), "MM/dd/yyyy")
                : "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.email}
            </Typography>
            {option.phone && (
              <Typography variant="caption" color="text.secondary">
                {formatInternational({ phoneNumber: option.phone })}
              </Typography>
            )}
          </Box>
        </li>
      )}
      filterOptions={(x) => x}
      sx={{ width: "100%" }}
    />
  );
};

export default AddCustomerToMergeSearch;
